import { mapActions, mapMutations, mapState } from 'vuex';
import get from 'lodash/get';
import set from 'lodash/set';
import merge from 'lodash/merge';
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import moment from 'moment';
import { yearMonthDayFormat } from '@enums/date-formats';
import overrideTypes from '@enums/override-types';
import transitionPeriodOverrideStates from '@enums/transition-period-override-states';
import { getReferencePriceAtOverride, getSmartOverrideFields } from '../store/utils/price-override';
import { getTsgKeyFromColId, getTsgKeyFromGroupId } from '../utils/tsg-utils';
/**
 * Bulk Price Override Selection Mixin
 *
 * ARCHITECTURE NOTE: Selection State Split
 * ========================================
 * This mixin manages component-local selection state whilst Vuex stores derived metadata.
 *
 * Component State (This Mixin):
 * - selectedRowsByTsg: { tsgKey: Set<productKey> } - Detailed selection data
 * - tsgHeaderCheckboxRefs: {} - UI component references
 * - All selection manipulation logic (toggle, clear, reconcile)
 *
 * Application State (Vuex gridView.bulkSelectionState):
 * - selectedCount, tsgKey, hasLock, multiTsg, isLoading - Derived metadata
 * - Used by engine-controls.vue and other components for UI coordination
 *
 * WHY THIS SPLIT?
 * - Selection sets are transient UI state (like form inputs), not application state
 * - Only derived summary needs cross-component sharing
 * - Encapsulation: Complex selection logic stays in focused mixin
 * - Performance: Avoids Vuex reactivity overhead on granular checkbox changes
 *
 * FLOW:
 * User clicks checkbox → selectedRowsByTsg updated → updateBulkSelectionState()
 * → derives summary → setBulkSelectionState(summary) → Vuex commit
 * → components react to bulkSelectionState changes
 *
 */

// Bulk action descriptors - declarative configuration for each action type
const BULK_ACTIONS = {
  /**
   * Override with proposed scenario price from the last engine run (XEO/SEO final price).
   * Uses the scenario price so promotions/locks already reflected in the engine output are honoured.
   */
  OVERRIDE_WITH_PROPOSED: {
    labelKey: 'gridView.bulkActions.overrideWithProposed',
    getPrice: (item, context) => get(item, context.getPriceField('scenarioPrice')),
    overrideType: overrideTypes.proposedPrice,
  },
  /**
   * Override with intention price (future price or live price fallback).
   * Always sets the price to intention price since items without existing overrides
   * cannot have smart override state (items with overrides are blocked upfront).
   */
  OVERRIDE_WITH_INTENTION: {
    labelKey: 'gridView.bulkActions.overrideWithIntention',
    getPrice: (item, context) => context.getIntentionPrice(item),
    overrideType: overrideTypes.intentionPrice,
  },
  /**
   * Remove all existing price overrides.
   * Reverts products back to their original pricing (intention or reference price).
   * Only processes items that currently have overrides.
   */
  REMOVE_OVERRIDES: {
    labelKey: 'gridView.bulkActions.removeAllOverrides',
    isRemove: true,
  },
  /**
   * Override with cost price delta scaling (XSR only).
   * Calculates proposed price by applying the cost delta ratio between reference date
   * and go-live costs to the intention price.
   */
  OVERRIDE_WITH_COST_PRICE_DELTA: {
    labelKey: 'gridView.bulkActions.overrideWithCostPriceDelta',
    getPrice: (item, context) => context.calculateCostDeltaPrice(item, 'cost'),
    overrideType: overrideTypes.costPriceDelta,
  },
  /**
   * Override with transfer price delta scaling (XSR only).
   * Calculates proposed price by applying the transfer price delta ratio between
   * reference date and go-live transfer prices to the intention price.
   */
  OVERRIDE_WITH_TRANSFER_PRICE_DELTA: {
    labelKey: 'gridView.bulkActions.overrideWithTransferPriceDelta',
    getPrice: (item, context) => context.calculateCostDeltaPrice(item, 'transferPrice'),
    overrideType: overrideTypes.transferPriceDelta,
  },
  /**
   * Override with purchase price delta scaling (XSR only).
   * Calculates proposed price by applying the purchase price delta ratio between
   * reference date and go-live purchase prices to the intention price.
   */
  OVERRIDE_WITH_PURCHASE_PRICE_DELTA: {
    labelKey: 'gridView.bulkActions.overrideWithPurchasePriceDelta',
    getPrice: (item, context) => context.calculateCostDeltaPrice(item, 'purchasePrice'),
    overrideType: overrideTypes.purchasePriceDelta,
  },
};

export default {
  data() {
    return {
      selectedRowsByTsg: {}, // Track selected rows per TSG: { tsgKey: Set<productKey> }
      tsgHeaderCheckboxRefs: {}, // Track header checkbox component instances per TSG
      isBulkConfirmDialogOpen: false,
      bulkConfirmData: {
        total: 0,
        blocked: 0,
        eligible: 0,
        actionLabel: '',
        isRemove: false,
      },
      pendingBulkAction: null, // Store the action to execute after confirmation
    };
  },
  computed: {
    ...mapState('gridView', {
      bulkSelectionState: state => state.bulkSelectionState,
    }),
  },
  watch: {
    lockedPricesByTsgKey: {
      handler(newLocks, oldLocks) {
        // Only process if bulk price overrides feature is enabled
        if (!this.isFeatureFlagEnabled('allowBulkPriceOverrides')) {
          return;
        }

        // Clear selections for TSGs that become locked
        if (!newLocks || !oldLocks) {
          return;
        }

        // Compute newly locked TSGs (where lock state changed from false/undefined to true)
        const newlyLocked = Object.keys(newLocks).filter(
          tsgKey => newLocks[tsgKey] === true && oldLocks[tsgKey] !== true
        );

        if (newlyLocked.length > 0) {
          // Clear selections for newly locked TSGs
          this.clearSelectionsForLockedTsgs(newlyLocked);
          // Refresh selection UI to update checkboxes and header state
          this.refreshSelectionUI(newlyLocked);
        }
        // Note: On unlock, disabled props automatically re-enable the UI via existing refresh logic
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations('gridView', ['setBulkSelectionState']),
    ...mapActions('gridView', ['updateOverridePrice']),

    /**
     * Determine if selection (checkboxes) should be disabled for a TSG.
     * Selection is disabled when the user lacks edit permissions OR the TSG is locked OR bulk override is in progress.
     * @param {string|null} tsgKey - Tool Store Group key
     * @returns {boolean} True if selection should be disabled
     */
    isSelectionDisabled(tsgKey) {
      if (!tsgKey) {
        return false;
      }

      const isBulkLoading = get(this.bulkSelectionState, 'isLoading', false);

      // Disable if user doesn't have permission OR if TSG is locked OR bulk override is in progress
      return (
        !this.isPriceOverrideEditable ||
        !!get(this.lockedPricesByTsgKey, tsgKey, false) ||
        isBulkLoading
      );
    },

    getNodeTsgKey(node) {
      return node.data && node.data.toolStoreGroupKey ? node.data.toolStoreGroupKey : null;
    },

    getTsgKeyFromColId,
    getTsgKeyFromGroupId,

    // Core selection state helpers

    /**
     * Get TSG keys that have non-empty selections
     * @returns {Array<string>} Array of TSG keys with active selections
     */
    getSelectedTsgKeys() {
      return Object.keys(this.selectedRowsByTsg).filter(
        tsgKey => this.selectedRowsByTsg[tsgKey] && this.selectedRowsByTsg[tsgKey].size > 0
      );
    },

    /**
     * Build selection column IDs for grid refresh based on TSG keys
     * @param {Array<string>} tsgKeys - TSG keys to build column IDs for
     * @returns {Array<string>} Array of column IDs for selection columns
     */
    getSelectionColumnIds(tsgKeys) {
      if (!tsgKeys || tsgKeys.length === 0) {
        return this.isGroupTSGsEnabled ? [] : ['selection'];
      }

      return this.isGroupTSGsEnabled
        ? tsgKeys.map(tsgKey => `toolStoreGroups.${tsgKey}.selection`)
        : ['selection'];
    },

    /**
     * Refresh selection UI (grid checkboxes and header checkboxes) for specific TSGs
     * @param {Array<string>} tsgKeys - TSG keys to refresh UI for
     */
    refreshSelectionUI(tsgKeys) {
      if (!this.gridApi || !tsgKeys || tsgKeys.length === 0) {
        return;
      }

      const selectionColumns = this.getSelectionColumnIds(tsgKeys);

      this.gridApi.refreshCells({
        columns: selectionColumns,
        force: true,
      });

      selectionColumns.forEach(colId => {
        this.updateHeaderCheckbox(colId);
      });
    },

    /**
     * Clear selections for specific TSGs
     * @param {Array<string>} tsgKeys - TSG keys to clear selections for
     */
    clearSelectionsForTsgs(tsgKeys) {
      if (!tsgKeys || tsgKeys.length === 0) {
        return;
      }

      tsgKeys.forEach(tsgKey => {
        if (this.selectedRowsByTsg[tsgKey]) {
          this.selectedRowsByTsg[tsgKey].clear();
        }
      });

      this.updateBulkSelectionState();
      this.refreshSelectionUI(tsgKeys);
    },

    // TSG-scoped selection helper methods
    // colId: column ID (e.g., "selection" or "toolStoreGroups.tsg1.selection")
    toggleSelectAllForTsg(colId, selectAll) {
      if (!this.gridApi) return;

      const targetTsgKey = this.isGroupTSGsEnabled ? this.getTsgKeyFromColId(colId) : null;

      // Block header select-all when the target TSG is locked
      if (this.isGroupTSGsEnabled && targetTsgKey && this.isSelectionDisabled(targetTsgKey)) {
        this.updateHeaderCheckbox(colId);
        return;
      }

      const addProductSelection = (tsgKey, productKey) => {
        if (!tsgKey) return;
        if (!this.selectedRowsByTsg[tsgKey]) {
          this.$set(this.selectedRowsByTsg, tsgKey, new Set());
        }
        this.selectedRowsByTsg[tsgKey].add(productKey);
      };

      if (selectAll) {
        this.gridApi.forEachNodeAfterFilter(node => {
          if (!node.data || !node.data.productKey) return;

          if (this.isGroupTSGsEnabled) {
            if (!targetTsgKey) return;
            // Skip adding selections for locked TSGs
            if (this.isSelectionDisabled(targetTsgKey)) return;
            addProductSelection(targetTsgKey, node.data.productKey);
            return;
          }

          const nodeTsgKey = this.getNodeTsgKey(node);
          if (!nodeTsgKey) return;
          // Skip adding selections for locked TSGs in non-grouped mode
          if (this.isSelectionDisabled(nodeTsgKey)) return;
          addProductSelection(nodeTsgKey, node.data.productKey);
        });

        const affectedTsgs = targetTsgKey ? [targetTsgKey] : this.getSelectedTsgKeys();
        this.updateBulkSelectionState();
        this.refreshSelectionUI(affectedTsgs);
      } else {
        const tsgsToClear =
          this.isGroupTSGsEnabled && targetTsgKey
            ? [targetTsgKey]
            : Object.keys(this.selectedRowsByTsg);

        this.clearSelectionsForTsgs(tsgsToClear);
      }
    },

    updateHeaderCheckbox(colId) {
      const headerCheckboxComponent = this.tsgHeaderCheckboxRefs[colId];
      if (headerCheckboxComponent && headerCheckboxComponent.updateState) {
        headerCheckboxComponent.updateState();
      }
    },

    /**
     * Get TSG keys that have visible columns in the grid
     * Uses the grid's column API to determine actual visibility, not data structure
     * @returns {Set<string>} Set of TSG keys with visible columns
     */
    getVisibleTsgKeys() {
      if (!this.gridApi) {
        return new Set();
      }

      const visibleTsgKeys = new Set();

      if (this.isGroupTSGsEnabled) {
        // In grouped mode, check for visible TSG columns
        // TSG columns have colId like: toolStoreGroups.{tsgKey}.{columnName}
        this.gridApi.getAllGridColumns().forEach(column => {
          if (column.isVisible()) {
            const tsgKey = getTsgKeyFromColId(column.getColId());
            if (tsgKey) {
              visibleTsgKeys.add(tsgKey);
            }
          }
        });
      } else {
        // In non-grouped mode, all TSGs in data are "visible"
        // Get unique TSG keys from the visible nodes
        this.gridApi.forEachNodeAfterFilter(node => {
          const nodeTsgKey = this.getNodeTsgKey(node);
          if (nodeTsgKey) {
            visibleTsgKeys.add(nodeTsgKey);
          }
        });
      }

      return visibleTsgKeys;
    },

    /**
     * Reconcile selection state by removing ghost selections for products/TSGs
     * that are no longer visible in the grid. This ensures accurate counts
     * after scope changes (filters, column visibility, TSG locks, etc.)
     *
     * Called from:
     * - Column visibility changes (including layouts)
     * - AG Grid filter changes
     * - Top-level filter changes (data refetch)
     * - TSG lock changes
     * - Grid destruction (beforeDestroy)
     */
    reconcileSelection() {
      if (!this.gridApi) {
        return;
      }

      const visibleTsgKeys = this.getVisibleTsgKeys();
      const visibleProductsByTsg = {};

      this.gridApi.forEachNodeAfterFilter(node => {
        if (!node.data || !node.data.productKey) return;

        if (this.isGroupTSGsEnabled) {
          const tsgKeys = Object.keys(node.data.toolStoreGroups || {});
          tsgKeys.forEach(tsgKey => {
            if (visibleTsgKeys.has(tsgKey)) {
              if (!visibleProductsByTsg[tsgKey]) {
                visibleProductsByTsg[tsgKey] = new Set();
              }
              visibleProductsByTsg[tsgKey].add(node.data.productKey);
            }
          });
        } else {
          const nodeTsgKey = this.getNodeTsgKey(node);
          if (nodeTsgKey && visibleTsgKeys.has(nodeTsgKey)) {
            if (!visibleProductsByTsg[nodeTsgKey]) {
              visibleProductsByTsg[nodeTsgKey] = new Set();
            }
            visibleProductsByTsg[nodeTsgKey].add(node.data.productKey);
          }
        }
      });

      let selectionsChanged = false;
      const changedTsgs = [];

      Object.keys(this.selectedRowsByTsg).forEach(tsgKey => {
        const selectedProducts = this.selectedRowsByTsg[tsgKey];
        if (!selectedProducts || selectedProducts.size === 0) return;

        const visibleProducts = visibleProductsByTsg[tsgKey];
        if (!visibleProducts) {
          selectedProducts.clear();
          selectionsChanged = true;
          changedTsgs.push(tsgKey);
          return;
        }

        selectedProducts.forEach(productKey => {
          if (!visibleProducts.has(productKey)) {
            selectedProducts.delete(productKey);
            selectionsChanged = true;
            if (!changedTsgs.includes(tsgKey)) {
              changedTsgs.push(tsgKey);
            }
          }
        });
      });

      if (selectionsChanged) {
        this.updateBulkSelectionState();
        this.refreshSelectionUI(changedTsgs);
      }

      // Even if selections did not change, header checkbox states may need
      // recalculation when visibility expands (more rows now visible).
      // Ensure header components re-evaluate checked/indeterminate state
      // for currently visible TSG selection columns.
      const visibleSelectionColumns = this.getSelectionColumnIds(Array.from(visibleTsgKeys));
      visibleSelectionColumns.forEach(colId => {
        this.updateHeaderCheckbox(colId);
      });
    },

    /**
     * Derive and commit selection summary metadata to Vuex store.
     *
     * This method bridges component-local state (selectedRowsByTsg) with application state
     * (Vuex bulkSelectionState). It computes summary metadata from detailed selection data
     * and commits to Vuex for cross-component consumption (e.g., engine-controls.vue).
     *
     * IMPORTANT: Always call this method after modifying selectedRowsByTsg to keep
     * Vuex state synchronised.
     *
     * Derived metadata:
     * - selectedCount: Total selected products across VISIBLE TSGs only
     * - tsgKey: Single TSG key if only one TSG has selections (null for multi-TSG)
     * - hasLock: Whether the single selected TSG is locked
     * - multiTsg: Whether selections span multiple TSGs (disables bulk actions)
     *
     * @returns {number} Total selection count
     */
    updateBulkSelectionState() {
      if (!this.gridApi) {
        return 0;
      }

      const selectedTsgKeys = this.getSelectedTsgKeys();
      const visibleTsgKeys = this.getVisibleTsgKeys();
      const visibleSelectedTsgKeys = selectedTsgKeys.filter(tsgKey => visibleTsgKeys.has(tsgKey));

      const count = visibleSelectedTsgKeys.reduce((total, tsgKey) => {
        return total + (this.selectedRowsByTsg[tsgKey] ? this.selectedRowsByTsg[tsgKey].size : 0);
      }, 0);

      const singleTsgKey = visibleSelectedTsgKeys.length === 1 ? visibleSelectedTsgKeys[0] : null;
      const multiTsg = visibleSelectedTsgKeys.length > 1;
      const hasLock = singleTsgKey ? get(this.lockedPricesByTsgKey, singleTsgKey, false) : false;

      this.setBulkSelectionState({
        selectedCount: count,
        tsgKey: singleTsgKey,
        hasLock,
        multiTsg,
      });

      return count;
    },

    /**
     * Collect full item data for selected products per TSG from the grid's filtered data.
     * In grouped mode, extracts TSG-specific data from toolStoreGroups object.
     * In non-grouped mode, uses the item directly.
     *
     * @returns {Array} Array of item objects ready for updateOverridePrice
     */
    getSelectedItemsData() {
      const items = [];

      if (!this.gridApi) return items;

      const selectedTsgKeys = Object.keys(this.selectedRowsByTsg);

      selectedTsgKeys.forEach(tsgKey => {
        const selectedProductKeys = this.selectedRowsByTsg[tsgKey];
        if (!selectedProductKeys || selectedProductKeys.size === 0) return;

        this.gridApi.forEachNodeAfterFilter(node => {
          if (!node.data || !node.data.productKey) return;

          const productKey = node.data.productKey;
          if (!selectedProductKeys.has(productKey)) return;

          if (this.isGroupTSGsEnabled) {
            // In grouped mode, extract TSG-specific data
            const tsgData = get(node.data, ['toolStoreGroups', tsgKey]);
            if (!tsgData) return;

            // Merge product-level data with TSG-specific data
            // Include all necessary fields for updateProductWithUnsavedKpiResults
            items.push({
              ...node.data,
              ...tsgData,
              toolStoreGroupKey: tsgKey,
            });
          } else {
            // In non-grouped mode, item has TSG data at top level
            const nodeTsgKey = this.getNodeTsgKey(node);
            if (nodeTsgKey === tsgKey) {
              items.push(node.data);
            }
          }
        });
      });

      return items;
    },

    /**
     * Get the single selected TSG key if only one TSG has selections
     * @returns {string|null} TSG key or null if multiple/no TSGs selected
     */
    getSingleSelectedTsgKey() {
      const selectedTsgKeys = this.getSelectedTsgKeys();
      return selectedTsgKeys.length === 1 ? selectedTsgKeys[0] : null;
    },

    /**
     * Check if selection spans multiple TSGs
     * @returns {boolean} True if multiple TSGs have selections
     */
    hasMultiTsgSelection() {
      return this.getSelectedTsgKeys().length > 1;
    },

    /**
     * Clear all selections across all TSGs and refresh grid checkboxes
     */
    clearAllSelections() {
      const allTsgKeys = Object.keys(this.selectedRowsByTsg);
      this.clearSelectionsForTsgs(allTsgKeys);
    },

    /**
     * Clear selections for specific TSGs that have become locked
     * @param {Array<string>} changedTsgKeys - Array of TSG keys that changed to locked
     */
    clearSelectionsForLockedTsgs(changedTsgKeys) {
      this.clearSelectionsForTsgs(changedTsgKeys);
    },

    /**
     * Main handler for bulk override operations
     * @param {Object} action - Action descriptor from BULK_ACTIONS
     */
    async applyBulkOverride(action) {
      const selectedItems = this.getSelectedItemsData();

      if (selectedItems.length === 0) {
        return;
      }

      // Derive isRemove flag by comparing action to REMOVE_OVERRIDES
      const isRemove = action === BULK_ACTIONS.REMOVE_OVERRIDES;

      // Check for existing overrides that would be blocked.
      // Active transition period overrides should not prevent a different override from being
      // created.
      const itemsWithOverrides = selectedItems.filter(
        item =>
          get(item, 'overridePrice.price', null) !== null &&
          !this.isActiveTransitionPeriodOverride(item)
      );

      // For removal, eligible items are those WITH overrides
      // For override actions, eligible items are those WITHOUT overrides
      const eligibleCount = isRemove
        ? itemsWithOverrides.length
        : selectedItems.length - itemsWithOverrides.length;

      const blockedCount = isRemove
        ? selectedItems.length - itemsWithOverrides.length
        : itemsWithOverrides.length;

      this.pendingBulkAction = { action, selectedItems };

      // Show confirmation dialog
      this.bulkConfirmData = {
        total: selectedItems.length,
        blocked: blockedCount,
        eligible: eligibleCount,
        actionLabel: this.$t(action.labelKey),
        isRemove,
      };
      this.isBulkConfirmDialogOpen = true;
    },

    /**
     * Build payload for a single item based on action type
     * @param {Object} item - Product item data
     * @param {Object} action - Action descriptor from BULK_ACTIONS
     * @returns {Object} Payload object for updateOverridePrice
     */
    buildItemPayload(item, action) {
      const intentionPrice = this.getIntentionPrice(item);

      if (action.isRemove === true) {
        // Remove override logic (mirrors handleRevertButtonClick)
        // hasUnsavedKpiResults indicates override NOT yet saved to Databricks (orange arrow)
        const isOverrideSavedToDatabricks = !item.hasUnsavedKpiResults;
        const referencePriceAtOverride = get(item, ['overridePrice', 'referencePriceAtOverride']);

        set(item, 'overridePrice', {
          price: null,
          overrideDate: null,
          overrideTimestamp: null,
          referencePriceAtOverride: null,
          expiryDate: null,
          expiryTimestamp: null,
          effectiveDate: null,
          effectiveTimestamp: null,
          isSmart: false,
          overrideType: null,
        });

        return {
          ...item,
          startingPrice: intentionPrice || referencePriceAtOverride,
          // revert flag controls MongoDB deletion vs recalculation for override removal:
          // - true: DELETE from MongoDB (priceOverrides + kpiResults collections) - for unsaved overrides (orange arrow)
          //   Result: Clean removal, no orange arrow after, nothing in Databricks to update
          // - false: RECALCULATE KPIs with null override and UPDATE MongoDB - for saved overrides (blue arrow)
          //   Result: Creates new kpiResults in MongoDB, orange arrow appears, requires "Save Changes" to persist removal to Databricks
          revert: !isOverrideSavedToDatabricks,
        };
      }

      // Override action - set new price
      const resolvedPrice = action.getPrice(item, this);
      const xeoEnabled = get(this.toggleLogic, 'enablePriceOptimisation', false);

      const defaultSmartOverrideFields = getSmartOverrideFields({
        goLiveDate: get(this.selectedWorkpackage, 'goLiveDate'),
        toggleLogic: this.toggleLogic,
      });
      const updatedOverridePriceObject = merge(
        {},
        get(item, 'overridePrice', {}),
        {
          price: resolvedPrice,
          overrideDate: moment().format(yearMonthDayFormat),
          overrideTimestamp: moment().unix(),
          referencePriceAtOverride: getReferencePriceAtOverride(xeoEnabled, item),
          isSmart: this.isSmartOverride(item),
          // Include override type for cost delta scaling actions
          overrideType: action.overrideType || null,
        },
        // only set default expiry/effective dates if not already a smart override.
        // Overwrite the expiry date for active transition period overrides to be the default.
        this.isSmartOverride(item) && !this.isActiveTransitionPeriodOverride(item)
          ? {}
          : defaultSmartOverrideFields
      );
      set(item, 'overridePrice', updatedOverridePriceObject);

      return {
        ...item,
        startingPrice: intentionPrice,
        usePromoPriceOnSmartOverride: false,
        revert: false,
      };
    },

    /**
     * Execute the bulk override after user confirmation
     */
    async executeBulkOverride() {
      if (!this.pendingBulkAction) return;

      const { action, selectedItems } = this.pendingBulkAction;
      this.isBulkConfirmDialogOpen = false;

      // Derive isRemove flag from action property
      const isRemove = action.isRemove === true;

      // For removal, process items WITH overrides
      // For override actions, process items WITHOUT overrides
      // Active transition period overrides should not prevent a different override from being
      // created, but should not be removable.
      const eligibleItems = isRemove
        ? selectedItems.filter(
            item =>
              get(item, 'overridePrice.price', null) !== null &&
              !this.isActiveTransitionPeriodOverride(item)
          )
        : selectedItems.filter(
            item =>
              get(item, 'overridePrice.price', null) === null ||
              this.isActiveTransitionPeriodOverride(item)
          );

      // Allow proceeding even if no eligible items - backend will handle gracefully
      // Capture the current row order before buildItemPayload mutates overridePrice on each item,
      // so postSortRows can freeze positions the same way a single price-edit save does.
      this.captureStableSortRowOrder();
      const itemsToUpdate = eligibleItems.map(item => this.buildItemPayload(item, action));

      this.setBulkSelectionState({ isLoading: true });

      // Refresh checkbox cells to reflect the disabled state during bulk operation
      const visibleTsgKeys = Array.from(this.getVisibleTsgKeys());
      const selectionColumns = this.getSelectionColumnIds(visibleTsgKeys);
      if (this.gridApi && selectionColumns.length > 0) {
        // Refresh header first to update header checkbox disabled state
        this.gridApi.refreshHeader();
        this.gridApi.refreshCells({
          columns: selectionColumns,
          force: true,
        });
      }

      try {
        await this.updateOverridePrice({
          items: itemsToUpdate,
        });

        // Clear selection after successful update
        this.clearAllSelections();
      } catch (error) {
        console.error('Bulk override failed:', error);
        // Error notification is handled by updateOverridePrice action
      } finally {
        // Always reset loading state
        this.setBulkSelectionState({ isLoading: false });

        // Refresh checkbox cells again to re-enable them after bulk operation completes
        if (this.gridApi && selectionColumns.length > 0) {
          // Refresh header first to update header checkbox disabled state
          this.gridApi.refreshHeader();
          this.gridApi.refreshCells({
            columns: selectionColumns,
            force: true,
          });
        }

        this.pendingBulkAction = null;
      }
    },

    /**
     * Cancel the bulk override confirmation
     */
    cancelBulkOverride() {
      this.isBulkConfirmDialogOpen = false;
      this.pendingBulkAction = null;
    },
    onBulkOverrideWithEnginePrice() {
      this.applyBulkOverride(BULK_ACTIONS.OVERRIDE_WITH_PROPOSED);
    },

    onBulkOverrideWithIntentionPrice() {
      this.applyBulkOverride(BULK_ACTIONS.OVERRIDE_WITH_INTENTION);
    },

    onBulkRemoveAllOverrides() {
      this.applyBulkOverride(BULK_ACTIONS.REMOVE_OVERRIDES);
    },

    onBulkOverrideWithCostPriceDelta() {
      this.applyBulkOverride(BULK_ACTIONS.OVERRIDE_WITH_COST_PRICE_DELTA);
    },

    onBulkOverrideWithTransferPriceDelta() {
      this.applyBulkOverride(BULK_ACTIONS.OVERRIDE_WITH_TRANSFER_PRICE_DELTA);
    },

    onBulkOverrideWithPurchasePriceDelta() {
      this.applyBulkOverride(BULK_ACTIONS.OVERRIDE_WITH_PURCHASE_PRICE_DELTA);
    },

    /**
     * Calculate the cost delta scaled price for a product.
     * Formula: intentionPrice * (goLiveCost / startCost)
     * Where startCost is referenceDateCost if the workpackage has a reference date, otherwise liveCost.
     * If the workpackage has a reference date but the product has no referenceDateCost, returns intentionPrice unchanged.
     * @param {Object} item - Product item data
     * @param {string} costType - Type of cost: 'cost', 'transferPrice', or 'purchasePrice'
     * @returns {number|null} Calculated price or null if insufficient data
     */
    calculateCostDeltaPrice(item, costType) {
      const intentionPrice = this.getIntentionPrice(item);
      if (!intentionPrice) {
        return null;
      }

      // Map cost type to field names
      const fieldMap = {
        cost: {
          referenceDate: 'referenceDateCost',
          live: 'liveCost',
          goLive: 'intentionCost',
        },
        transferPrice: {
          referenceDate: 'referenceDateTransferPrice',
          live: 'liveTransferPrice',
          goLive: 'intentionTransferPrice',
        },
        purchasePrice: {
          referenceDate: 'referenceDatePurchasePrice',
          live: 'livePurchasePrice',
          goLive: 'intentionPurchasePrice',
        },
      };

      const fields = fieldMap[costType];
      if (!fields) {
        console.warn('Unknown cost type:', costType);
        return null;
      }

      // Use referenceDateCost if workpackage has a reference date, otherwise use liveCost
      const hasWorkpackageReferenceDate = !!get(this.selectedWorkpackage, 'referenceDate');
      const startCostField = hasWorkpackageReferenceDate ? fields.referenceDate : fields.live;
      const startCost = get(item, `${startCostField}.price`, null);

      // Get go-live cost (intention value)
      const goLiveCost = get(item, `${fields.goLive}.price`, null);

      // If either cost is missing/zero, we cannot calculate delta - return intention price unchanged
      if (!startCost || isNil(goLiveCost)) {
        return intentionPrice;
      }

      // Calculate cost delta ratio and apply to intention price
      const costDeltaRatio = goLiveCost / startCost;
      const scaledPrice = intentionPrice * costDeltaRatio;

      // Round to nearest cent
      return round(scaledPrice, 2);
    },

    /**
     * Route bulk actions from engine-controls component
     * @param {string} action - Action type from bulkPriceOverrideActions enum
     */
    handleBulkAction(action) {
      const bulkPriceOverrideActions = {
        overrideWithEnginePrice: 'override-with-engine-price',
        overrideWithIntentionPrice: 'override-with-intention-price',
        removeAllOverrides: 'remove-all-overrides',
        overrideWithCostPriceDelta: 'override-with-cost-price-delta',
        overrideWithTransferPriceDelta: 'override-with-transfer-price-delta',
        overrideWithPurchasePriceDelta: 'override-with-purchase-price-delta',
      };

      switch (action) {
        case bulkPriceOverrideActions.overrideWithEnginePrice:
          this.onBulkOverrideWithEnginePrice();
          break;
        case bulkPriceOverrideActions.overrideWithIntentionPrice:
          this.onBulkOverrideWithIntentionPrice();
          break;
        case bulkPriceOverrideActions.removeAllOverrides:
          this.onBulkRemoveAllOverrides();
          break;
        case bulkPriceOverrideActions.overrideWithCostPriceDelta:
          this.onBulkOverrideWithCostPriceDelta();
          break;
        case bulkPriceOverrideActions.overrideWithTransferPriceDelta:
          this.onBulkOverrideWithTransferPriceDelta();
          break;
        case bulkPriceOverrideActions.overrideWithPurchasePriceDelta:
          this.onBulkOverrideWithPurchasePriceDelta();
          break;
        default:
          console.warn('Unknown bulk price override action:', action);
      }
    },

    isSmartOverride(item) {
      return get(item, 'overridePrice.expiryDate', null) !== null;
    },

    /**
     * Determines if the given price override is an active transition period override.
     * @param {Object} item - The price override item.
     * @returns True if the override is a transition period override and its state is active, False otherwise.
     */
    isActiveTransitionPeriodOverride(item) {
      return (
        get(item, 'overridePrice.overrideType', null) === overrideTypes.transitionPeriod &&
        get(item, 'transitionPeriodOverrideMetadata.state', null) ===
          transitionPeriodOverrideStates.active
      );
    },
  },
};
